'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CivicEvent, CivicEventInput, CivicPresence } from '../lib/civic-events'

type UseCivicRealtimeOptions = {
  channels?: string[]
  district?: string
  masjidId?: string
  role?: string
  userId?: string
  enabled?: boolean
  limit?: number
}

type ReplayResponse = {
  events?: unknown[]
  presence?: unknown[]
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function safeString(value: unknown, fallback: string) {
  return typeof value === 'string' && value.trim() ? value : fallback
}

function safeNumber(value: unknown, fallback: number) {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

function normalizeEvent(value: unknown): CivicEvent | null {
  if (!isRecord(value)) return null
  const type = safeString(value.type, '')
  const title = safeString(value.title, '')
  if (!type || !title) return null

  const actor = isRecord(value.actor) ? value.actor : {}
  const scope = isRecord(value.scope) ? value.scope : {}
  const trust = isRecord(value.trust) ? value.trust : {}
  const summary = isRecord(value.summary) ? value.summary : {}

  return {
    id: safeString(value.id, `event_${Date.now()}`),
    type: type as CivicEvent['type'],
    title,
    body: safeString(value.body, title),
    occurredAt: safeString(value.occurredAt, new Date().toISOString()),
    priority: (value.priority === 'critical' || value.priority === 'high' || value.priority === 'medium' || value.priority === 'low' ? value.priority : 'medium') as CivicEvent['priority'],
    scope: {
      district: typeof scope.district === 'string' ? scope.district : undefined,
      city: typeof scope.city === 'string' ? scope.city : undefined,
      masjidId: typeof scope.masjidId === 'string' ? scope.masjidId : undefined,
      campaignId: typeof scope.campaignId === 'string' ? scope.campaignId : undefined,
      waqfId: typeof scope.waqfId === 'string' ? scope.waqfId : undefined,
      userId: typeof scope.userId === 'string' ? scope.userId : undefined,
      role: typeof scope.role === 'string' ? scope.role : undefined,
    },
    actor: {
      id: safeString(actor.id, 'system:unknown'),
      type: (actor.type === 'user' || actor.type === 'system' || actor.type === 'committee' || actor.type === 'moderator' || actor.type === 'volunteer' || actor.type === 'donor' || actor.type === 'ai' ? actor.type : 'system') as CivicEvent['actor']['type'],
      name: safeString(actor.name, 'Civic system'),
    },
    entityIds: Array.isArray(value.entityIds) ? value.entityIds.filter((item): item is string => typeof item === 'string') : [],
    channels: Array.isArray(value.channels) ? value.channels.filter((item): item is string => typeof item === 'string') : ['global', `type:${type}`],
    trust: {
      score: safeNumber(trust.score, 75),
      confidence: safeNumber(trust.confidence, 70),
      impact: safeNumber(trust.impact, 60),
      propagatedFrom: Array.isArray(trust.propagatedFrom) ? trust.propagatedFrom.filter((item): item is string => typeof item === 'string') : undefined,
    },
    summary: {
      headline: safeString(summary.headline, title),
      short: safeString(summary.short, safeString(value.body, title)),
      anomaly: typeof summary.anomaly === 'string' ? summary.anomaly : undefined,
      escalation: typeof summary.escalation === 'string' ? summary.escalation : undefined,
      districtPacket: typeof summary.districtPacket === 'string' ? summary.districtPacket : undefined,
    },
    provenance: {
      source: 'system',
      traceId: safeString(isRecord(value.provenance) ? value.provenance.traceId : undefined, `trace_${Date.now()}`),
    },
    metadata: isRecord(value.metadata) ? value.metadata : {},
  }
}

function normalizePresence(value: unknown): CivicPresence | null {
  if (!isRecord(value)) return null
  const kind = value.kind === 'volunteer' || value.kind === 'donor' || value.kind === 'committee' || value.kind === 'moderator' || value.kind === 'prayer_crowd' || value.kind === 'district' ? value.kind : null
  if (!kind) return null
  return {
    id: safeString(value.id, `presence_${Date.now()}`),
    label: safeString(value.label, 'Live civic presence'),
    kind,
    scope: isRecord(value.scope) ? value.scope : {},
    count: safeNumber(value.count, 0),
    status: (value.status === 'active' || value.status === 'surging' || value.status === 'quiet' || value.status === 'offline' ? value.status : 'quiet') as CivicPresence['status'],
    updatedAt: safeString(value.updatedAt, new Date().toISOString()),
    trust: safeNumber(value.trust, 70),
  }
}

function queryString(options: UseCivicRealtimeOptions, afterId?: string) {
  const params = new URLSearchParams()
  if (options.channels?.length) params.set('channels', options.channels.join(','))
  if (options.district) params.set('district', options.district)
  if (options.masjidId) params.set('masjidId', options.masjidId)
  if (options.role) params.set('role', options.role)
  if (options.userId) params.set('userId', options.userId)
  if (options.limit) params.set('limit', String(options.limit))
  if (afterId) params.set('afterId', afterId)
  return params.toString()
}

export function useCivicRealtime(options: UseCivicRealtimeOptions = {}) {
  const { enabled = true, limit = 40 } = options
  const [events, setEvents] = useState<CivicEvent[]>([])
  const [presence, setPresence] = useState<CivicPresence[]>([])
  const [status, setStatus] = useState<'connecting' | 'connected' | 'reconnecting' | 'polling' | 'offline'>('connecting')
  const lastIdRef = useRef<string | undefined>()
  const bufferRef = useRef<CivicEvent[]>([])
  const flushRef = useRef<number | null>(null)
  const sourceRef = useRef<EventSource | null>(null)

  const stableKey = JSON.stringify({
    channels: options.channels || [],
    district: options.district || '',
    masjidId: options.masjidId || '',
    role: options.role || '',
    userId: options.userId || '',
    enabled,
    limit,
  })
  const stableOptions = useMemo<UseCivicRealtimeOptions>(() => JSON.parse(stableKey), [stableKey])

  const flush = useCallback(() => {
    flushRef.current = null
    const batch = bufferRef.current.splice(0)
    if (!batch.length) return
    setEvents((current) => {
      const merged = [...batch, ...current]
      const seen = new Set<string>()
      return merged.filter((event) => {
        if (seen.has(event.id)) return false
        seen.add(event.id)
        return true
      }).slice(0, limit)
    })
    lastIdRef.current = batch[0]?.id || lastIdRef.current
  }, [limit])

  const queueEvent = useCallback((event: CivicEvent | unknown) => {
    const normalized = normalizeEvent(event)
    if (!normalized) return
    bufferRef.current.unshift(normalized)
    window.dispatchEvent(new CustomEvent('masjids:civic-event', { detail: normalized }))
    if (flushRef.current) return
    flushRef.current = window.setTimeout(flush, 450)
  }, [flush])

  const replay = useCallback(async () => {
    const params = queryString({ ...stableOptions, limit }, lastIdRef.current)
    const response = await fetch(`/api/realtime/replay?${params}`, { cache: 'no-store' })
    if (!response.ok) return
    const data = await response.json() as ReplayResponse
    if (Array.isArray(data.presence)) setPresence(data.presence.map(normalizePresence).filter((item): item is CivicPresence => Boolean(item)))
    if (Array.isArray(data.events) && data.events.length) {
      data.events.slice().reverse().forEach(queueEvent)
    }
  }, [limit, queueEvent, stableOptions])

  const dispatch = useCallback(async (input: CivicEventInput) => {
    const optimistic = {
      id: `optimistic_${Date.now()}`,
      type: input.type,
      title: input.title,
      body: input.body || input.title,
      occurredAt: new Date().toISOString(),
      priority: input.priority || 'medium',
      scope: input.scope || {},
      actor: input.actor || { id: 'ui:optimistic', type: 'user', name: 'You' },
      entityIds: input.entityIds || [],
      channels: ['global', `type:${input.type}`],
      trust: { score: input.trust?.score || 80, confidence: input.trust?.confidence || 70, impact: input.trust?.impact || 70 },
      summary: { headline: input.summary?.headline || input.title, short: input.summary?.short || input.body || input.title },
      provenance: { source: 'ui', traceId: `trace_${Date.now()}` },
      metadata: input.metadata || {},
    } as CivicEvent
    queueEvent(optimistic)

    const response = await fetch('/api/realtime/dispatch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    })
    if (!response.ok) throw new Error('Unable to dispatch civic event')
    return response.json()
  }, [queueEvent])

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return
    let polling: number | null = null
    let closed = false

    const connect = () => {
      sourceRef.current?.close()
      setStatus(navigator.onLine ? 'connecting' : 'offline')
      const params = queryString({ ...stableOptions, limit }, lastIdRef.current)
      const source = new EventSource(`/api/realtime/events?${params}`)
      sourceRef.current = source

      source.addEventListener('open', () => {
        if (polling) {
          window.clearInterval(polling)
          polling = null
        }
        setStatus('connected')
      })
      source.addEventListener('replay', (message) => {
        try {
          const data = JSON.parse((message as MessageEvent).data) as ReplayResponse
          if (Array.isArray(data.presence)) setPresence(data.presence.map(normalizePresence).filter((item): item is CivicPresence => Boolean(item)))
          if (Array.isArray(data.events)) data.events.slice().reverse().forEach(queueEvent)
        } catch {
          // Ignore malformed realtime frames instead of breaking the client runtime.
        }
      })
      source.addEventListener('presence', (message) => {
        try {
          const data = JSON.parse((message as MessageEvent).data) as ReplayResponse
          if (Array.isArray(data.presence)) setPresence(data.presence.map(normalizePresence).filter((item): item is CivicPresence => Boolean(item)))
        } catch {
          // Ignore malformed presence frames.
        }
      })
      source.addEventListener('civic-event', (message) => {
        try {
          queueEvent(JSON.parse((message as MessageEvent).data))
        } catch {
          // Ignore malformed realtime frames.
        }
      })
      source.onerror = () => {
        if (closed) return
        setStatus(navigator.onLine ? 'reconnecting' : 'offline')
        if (!polling) {
          polling = window.setInterval(() => {
            setStatus(navigator.onLine ? 'polling' : 'offline')
            replay().catch(() => {})
          }, 10000)
        }
      }
    }

    const onOnline = () => {
      if (polling) {
        window.clearInterval(polling)
        polling = null
      }
      connect()
    }
    const onOffline = () => setStatus('offline')

    connect()
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)

    return () => {
      closed = true
      sourceRef.current?.close()
      if (polling) window.clearInterval(polling)
      if (flushRef.current) window.clearTimeout(flushRef.current)
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
    }
  }, [enabled, flush, limit, queueEvent, replay, stableKey, stableOptions])

  return {
    events,
    presence,
    status,
    latestEvent: events[0],
    dispatch,
    replay,
  }
}
